import { QuestionMarkIcon } from "@components/Icons/QuestionMark";
import { FirebaseCachedImage } from "@components/FirebaseCachedImage";
import { FullPicker } from "@components/ImagePicker/FullPicker";
import { MediaCarousel } from "@components/MediaCarousel";
import { Share } from "@components/Share";
import { Timestamp, doc, updateDoc, getDoc, writeBatch } from "@firebase/firestore";
import { useTranslatedKey } from "@helpers/useTranslatedKey";
import { useTranslation } from "@helpers/useTranslation";
import { useProgressStore } from "@pages/challenges";
import { ref as storageRef, uploadBytes } from "firebase/storage";
import { FC, useContext, useEffect, useState, useMemo } from "react";
import { useHttpsCallable } from "react-firebase-hooks/functions";
import { FirebaseContext } from "src/helpers/firebase";
import { useConfetti } from "src/helpers/useConfetti";
import { useEventNavigate } from "src/helpers/useEventNavigate";
import { useTeam } from "src/helpers/useTeam";
import { useToast } from "src/helpers/useToast";
import { createExtraMapEntry, getChallengeLinkedExtras } from "src/helpers/extrasMapHelper";
import { uid } from "src/helpers/uid";

interface Props {
  challenge: PhotoChallenge;
  completed: boolean;
  teamId: string;
  eventId: string;
}

export const Photo: FC<Props> = (props) => {
  const { id } = props.challenge;
  const { completed, eventId, teamId } = props;
  const { t } = useTranslation("challenges");
  const { firestore, functions, storage } = useContext(FirebaseContext);
  const [image, setImage] = useState(undefined);
  const navigate = useEventNavigate();
  const [team] = useTeam();
  const [submitting, setSubmitting] = useState(false);
  const [uploadingMore, setUploadingMore] = useState(false);
  const [showMorePhotosUpload, setShowMorePhotosUpload] = useState(false);
  const [additionalImages, setAdditionalImages] = useState<File[]>([]);
  const [uploadKey, setUploadKey] = useState(0); // Key to reset FullPicker after upload
  useConfetti("submit-button");
  const toast = useToast();
  const [updateTeamPoints, _, error] = useHttpsCallable<API.UpdateTeamPointsRequest, API.UpdateTeamPointsResponse>(
    functions,
    "updateTeamPointsV2",
  );

  // @ts-ignore
  const setChallengeLoadingState = useProgressStore((state) => state.setChallenge);

  const { getTranslatedValue } = useTranslatedKey();
  const title = getTranslatedValue(props.challenge, "title");
  const copy = getTranslatedValue(props.challenge, "copy");

  // Get challenge-linked extras for this challenge
  const challengeExtras = useMemo(() => {
    if (!team?.extrasMap) return [];
    return getChallengeLinkedExtras(team.extrasMap, id);
  }, [team?.extrasMap, id]);

  const carouselItems: MediaItem[] = useMemo(() => {
    const items: MediaItem[] = [{ id, type: "photo", uri: `events/${eventId}/teams/${teamId}/solutions/${id}` }];
    challengeExtras.forEach((extraId) => {
      items.push({ id: extraId, type: "photo", uri: `events/${eventId}/teams/${teamId}/extras/${extraId}` });
    });
    return items;
  }, [id, eventId, teamId, challengeExtras]);

  useEffect(() => {
    if (error) {
      toast(t("problemOccured"), "🤷‍♂️", "error");
      console.error("Problem occured in updateTeamPoints function: ", error);
    }
  }, [error]);

  const updatePromise = async () => {
    try {
      setChallengeLoadingState(id, true);
      // Upload images
      const imagePath = `events/${eventId}/teams/${teamId}/solutions/${id}`;
      const imageRef = storageRef(storage, imagePath);
      await uploadBytes(imageRef, image);

      // Update firestore
      await updateTeamPoints({ teamId, challengeId: id, eventId, solutionType: "photo", instagramConfirmed });
      const teamDocRef = doc(firestore, `events/${eventId}/teams/${teamId}`);

      // Get solutions map and add the current challenge id to it
      const teamDoc = await getDoc(teamDocRef);
      const teamData = teamDoc.data();
      const solutionsMap = teamData?.solutionsMap || {};
      solutionsMap[id] = Timestamp.now();
      await updateDoc(teamDocRef, { solutionsMap });
    } catch (error) {
      toast(t("problemOccured"), "🤷‍♂️", "error");
      console.error(error);
    } finally {
      // Give time to photo proccessing lambda to finish
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setChallengeLoadingState(id, false);
    }
  };

  const submit = async () => {
    if (!team) {
      toast(t("problemOccured"), "🤷‍♂️", "error");
      console.error(`Team is undefined. Team: ${team}, event: ${eventId}`);
      return;
    }

    setSubmitting(true);
    updatePromise(); // Don't wait for this to finish for better UX
    navigate("/challenges");
  };

  const isVestuviuTurasEvent = eventId === "vestuviuturas";
  const teamHasInstagram = team?.instagram;

  const [instagramConfirmed, setInstagramConfirmed] = useState(false);

  const uploadMorePhotos = async () => {
    if (!team || !additionalImages[0]) {
      toast(t("problemOccured"), "🤷‍♂️", "error");
      return;
    }

    setUploadingMore(true);

    try {
      const extraId = uid();
      const imagePath = `events/${eventId}/teams/${teamId}/extras/${extraId}`;
      const imageRef = storageRef(storage, imagePath);

      await uploadBytes(imageRef, additionalImages[0]);

      // Create extras collection document
      const batch = writeBatch(firestore);
      const docRef = doc(firestore, `events/${eventId}/teams/${teamId}/extras/${extraId}`);
      batch.set(docRef, {
        id: extraId,
        createdOn: Timestamp.now(),
        challengeId: id, // Link to the challenge
      });

      // Update team's extrasMap with challenge-linked entry
      const teamDocRef = doc(firestore, `events/${eventId}/teams/${teamId}`);
      const teamDoc = await getDoc(teamDocRef);
      const teamData = teamDoc.data();
      const extrasMap = teamData?.extrasMap || {};

      extrasMap[extraId] = createExtraMapEntry(id);

      await updateDoc(teamDocRef, { extrasMap });
      await batch.commit();

      setUploadKey((prev) => prev + 1); // Reset FullPicker to clear preview

      // Give time for photo processing
      await new Promise((resolve) => setTimeout(resolve, 2000));

      toast(t("photoUploaded") || "Photo uploaded!", "📸", "success");
      setAdditionalImages([]);
      // Keep upload UI open so user can add more
    } catch (error) {
      console.error("Error uploading more photo:", error);
      toast(t("problemOccured"), "🤷‍♂️", "error");
    } finally {
      setUploadingMore(false);
    }
  };

  return (
    <>
      <h1 className="mx-4 mt-16 mb-8 text-center text-4xl font-semibold">{title}</h1>
      <div className="mt-4 mb-8 flex flex-col items-center">
        <div className="mx-6">
          {completed ? (
            <div className="flex flex-col items-center">
              <div className="flex max-w-lg flex-col justify-center">
                <h2 className="text-center text-xl font-bold">{t("challengeDone")}</h2>
                <div className="mt-4 mb-4 text-center text-lg">{t("challengeDoneSummary")}</div>
              </div>
              <div className="px-12 py-6">
                <Share title={title} text={title} uri={`events/${eventId}/teams/${teamId}/solutions/${id}`} />
                <MediaCarousel items={carouselItems} />

                {/* Add more photos button */}
                {!showMorePhotosUpload && (
                  <div className="mt-6 flex justify-center">
                    <button onClick={() => setShowMorePhotosUpload(true)} className="btn-outline btn">
                      📸 {t("addMorePhotos") || "Add More Photos"}
                    </button>
                  </div>
                )}

                {/* Additional photos upload UI */}
                {showMorePhotosUpload && (
                  <div className="mt-6">
                    <p className="mb-4 text-center text-sm opacity-70">
                      {t("addMorePhotosDescription") ||
                        "Upload additional photos for this challenge. You can upload one photo at a time."}
                    </p>
                    <FullPicker
                      key={uploadKey} // Reset component after each upload
                      uploadLabel={t("selectPhoto") || "Select Photo"}
                      reuploadLabel={t("selectDifferentPhoto") || "Select Different Photo"}
                      onNewImage={(img) => {
                        if (img) setAdditionalImages([img]);
                      }}
                      allowUpload
                    />
                    <div className="mt-4 flex justify-center gap-4">
                      <button
                        disabled={additionalImages.length === 0 || uploadingMore}
                        onClick={uploadMorePhotos}
                        className={`btn-primary btn ${uploadingMore ? "loading" : ""}`}
                      >
                        {t("uploadPhoto") || "Upload Photo"}
                      </button>
                      <button
                        onClick={() => {
                          setShowMorePhotosUpload(false);
                          setAdditionalImages([]);
                        }}
                        className="btn-outline btn"
                        disabled={uploadingMore}
                      >
                        {t("done") || "Done"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <p className="text-md mt-6 mb-4 text-center">{copy}</p>
              {props.challenge.introPhotoUri && (
                <div className="mb-4 flex justify-center">
                  <div className="w-full max-w-xs">
                    <FirebaseCachedImage uri={props.challenge.introPhotoUri} alt={title} />
                  </div>
                </div>
              )}
              <FullPicker uploadLabel={t("upload")} reuploadLabel={t("reupload")} onNewImage={setImage} allowUpload />
            </>
          )}
          {isVestuviuTurasEvent && !completed && teamHasInstagram && (
            <div>
              <div className="flex w-full flex-row justify-center">
                <div
                  className="tooltip-center tooltip tooltip-top -mr-1 mt-4 mb-4 w-6"
                  data-tip={t("confirmInstagramNote")}
                >
                  <QuestionMarkIcon className="h-8 h-8 text-neutral opacity-60" strokeWidth={0.2} />
                </div>
              </div>
              <div className="mb-8 flex justify-center">
                <label className="label cursor-pointer">
                  <span className="label-text mr-3 text-center text-sm">{t("confirmInstagram")}</span>
                  <input
                    type="checkbox"
                    className="toggle-primary toggle"
                    defaultChecked={instagramConfirmed}
                    onClick={(e) => {
                      const value = (e.target as any).checked;
                      setInstagramConfirmed(value);
                    }}
                  />
                </label>
              </div>
            </div>
          )}
          <div className="mb-12 flex justify-center">
            <button
              disabled={!image || submitting}
              onClick={submit}
              className={`btn-primary btn ${submitting ? "loading" : ""}`}
              id="submit-button"
            >
              {t("submit")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
